import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  LINK: "#60a5fa",
  LINK_HOVER: "#93c5fd",
  CODE_BG: "#1e1e1e",
};

const StyledMarkdown = styled(Box)(({ theme }) => ({
  color: DARK_COLORS.TEXT_PRIMARY,
  lineHeight: 1.6,
  
  '& p': {
    margin: '0.5em 0',
    '&:first-of-type': {
      marginTop: 0,
    },
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: DARK_COLORS.TEXT_PRIMARY,
    fontWeight: 600,
    marginTop: '1.2em',
    marginBottom: '0.6em',
    '&:first-child': {
      marginTop: 0,
    },
  },
  
  '& h1': { fontSize: '1.8em', borderBottom: `2px solid ${DARK_COLORS.BORDER}`, paddingBottom: '0.3em' },
  '& h2': { fontSize: '1.5em', borderBottom: `1px solid ${DARK_COLORS.BORDER}`, paddingBottom: '0.3em' },
  '& h3': { fontSize: '1.3em' },
  '& h4': { fontSize: '1.1em' },
  
  '& a': {
    color: DARK_COLORS.LINK,
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: DARK_COLORS.LINK_HOVER,
      borderBottomColor: DARK_COLORS.LINK_HOVER,
    },
  },
  
  '& ul, & ol': {
    paddingLeft: '1.5em',
    margin: '0.5em 0',
  },
  
  '& li': {
    margin: '0.3em 0',
  },
  
  '& code': {
    backgroundColor: DARK_COLORS.CODE_BG,
    color: '#e879f9',
    padding: '0.2em 0.4em',
    borderRadius: '4px',
    fontSize: '0.9em',
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
  },
  
  '& pre': {
    backgroundColor: DARK_COLORS.CODE_BG,
    padding: '1em',
    borderRadius: '8px',
    overflow: 'auto',
    border: `1px solid ${DARK_COLORS.BORDER}`,
    margin: '1em 0',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
      fontSize: '0.85em',
    },
  },
  
  '& blockquote': {
    borderLeft: `4px solid ${DARK_COLORS.LINK}`,
    paddingLeft: '1em',
    margin: '1em 0',
    color: DARK_COLORS.TEXT_SECONDARY,
    fontStyle: 'italic',
  },
  
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '1em 0',
    border: `1px solid ${DARK_COLORS.BORDER}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  
  '& th': {
    backgroundColor: DARK_COLORS.SECONDARY,
    color: DARK_COLORS.TEXT_PRIMARY,
    fontWeight: 600,
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: `2px solid ${DARK_COLORS.BORDER}`,
  },
  
  '& td': {
    padding: '8px 16px',
    borderBottom: `1px solid ${DARK_COLORS.BORDER}`,
    color: DARK_COLORS.TEXT_PRIMARY,
  },
  
  '& tr': {
    '&:nth-of-type(odd)': {
      backgroundColor: DARK_COLORS.SURFACE,
    },
    '&:nth-of-type(even)': {
      backgroundColor: DARK_COLORS.ACCENT,
    },
    '&:hover': {
      backgroundColor: DARK_COLORS.HOVER,
    },
    transition: 'background-color 0.2s ease',
  },
  
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${DARK_COLORS.BORDER}`,
    margin: '1.5em 0',
  },
  
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
    margin: '1em 0',
  },
  
  '& strong': {
    fontWeight: 600,
    color: DARK_COLORS.TEXT_PRIMARY,
  },
  
  '& em': {
    fontStyle: 'italic',
    color: DARK_COLORS.TEXT_SECONDARY,
  },
  
  // Специальные стили для подсказок
  '&.markdown-hint': {
    fontSize: '0.95em',
    fontStyle: 'italic',
    opacity: 0.9,
    marginTop: '12px',
    padding: '12px',
    backgroundColor: 'rgba(96, 165, 250, 0.1)',
    borderLeft: `3px solid ${DARK_COLORS.LINK}`,
    borderRadius: '0 8px 8px 0',
  },
}));

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
  return (
    <StyledMarkdown className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, ...props }: any) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
              {props.href && !props.children?.toString().includes('↗') && ' ↗'}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </StyledMarkdown>
  );
};

export default MarkdownRenderer;
